/**
 * Z-Order Behavior
 *
 * Tracks maxZ across all nodes and brings newly selected nodes to front.
 * When a node is selected for the first time, its z-order is set to maxZ+1.
 */

import type { EditorNode, ViewState } from '../types';

// =============================================================================
// Types
// =============================================================================

export interface ZOrderBehaviorContext {
	getNodes: () => EditorNode[];
	getViewState: () => ViewState;
}

export interface ZOrderManager {
	/** Get the current max z-order */
	getMaxZ(): number;

	/** Update maxZ from current nodes (call after setNodes) */
	syncMaxZ(): void;

	/**
	 * Bring newly selected nodes to front by updating their z-order.
	 * Call this when selection changes, passing previous and new selection.
	 */
	bringToFrontOnSelection(prevSelection: Set<string>, newSelection: Set<string>): void;
}

// =============================================================================
// Implementation
// =============================================================================

export function createZOrderManager(ctx: ZOrderBehaviorContext): ZOrderManager {
	let maxZ = 0;

	// Initialize maxZ from current nodes
	function syncMaxZ(): void {
		maxZ = 0;
		for (const node of ctx.getNodes()) {
			if (node.z !== undefined && node.z > maxZ) {
				maxZ = node.z;
			}
		}
	}

	// Initialize on creation
	syncMaxZ();

	return {
		getMaxZ(): number {
			return maxZ;
		},

		syncMaxZ,

		bringToFrontOnSelection(prevSelection: Set<string>, newSelection: Set<string>): void {
			const nodes = ctx.getNodes();
			for (const nodeId of newSelection) {
				if (!prevSelection.has(nodeId)) {
					const node = nodes.find((n) => n.id === nodeId);
					if (node) {
						maxZ++;
						node.z = maxZ;
					}
				}
			}
		},
	};
}
